import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Mail, ArrowRight } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Passwordless entry: trusted devices go straight through, others get a one-time code. */
export default function PortalAccess() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const [name, setName] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [retryable, setRetryable] = useState(false);
  const [busy, setBusy] = useState(false);
  const claimed = useRef(false);

  useEffect(() => {
    if (claimed.current) return;
    claimed.current = true;
    (async () => {
      try {
        const { data } = await api.post("/portal/claim", { token });
        setName(data.name || "");
        if (data.signed_in) {
          await refresh?.();
          navigate("/", { replace: true });
          return;
        }
        setMaskedEmail(data.code_sent_to || "");
        setState("code");
      } catch (e) {
        const status = e?.response?.status;
        setRetryable(!status || status >= 500 || status === 429);
        setError(status && status < 500 && status !== 429
          ? apiError(e)
          : "We couldn’t open your portal just now. Please try again in a moment.");
        setState("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/portal/verify", { token, code: code.trim() });
      await refresh?.();
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError("");
    try {
      const { data } = await api.post("/portal/resend-code", { token });
      setMaskedEmail(data.code_sent_to || maskedEmail);
      setCode("");
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bone px-6 py-16" data-testid="portal-access-page">
      <div className="w-full max-w-md">
        <div className="mb-10">
          <div className="overline mb-3">Ästhetik Design Co.</div>
          <h1 className="font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl">
            {state === "code" ? `Welcome back${name ? `, ${name.split(" ")[0]}` : ""}.` : "Your project portal"}
          </h1>
        </div>

        {state === "checking" && (
          <div className="flex items-center gap-3 text-sm text-stone" data-testid="portal-checking">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Opening your portal…
          </div>
        )}

        {state === "code" && (
          <form onSubmit={verify} className="card-surface p-7" data-testid="portal-code-form">
            <p className="flex items-start gap-2 text-sm leading-relaxed text-stone">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-lime-dark" strokeWidth={1.5} />
              <span>We’ve sent a six-digit code to <span className="num">{maskedEmail}</span>. It’s valid for 15 minutes.</span>
            </p>
            <input
              data-testid="portal-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoFocus
              placeholder="000000"
              className="num mt-7 w-full rounded-sm border border-line bg-white px-4 py-3.5 text-center text-2xl tracking-[0.4em] outline-none transition-colors focus:border-lime"
            />
            {error && <p data-testid="portal-error" className="mt-4 text-sm text-[#A8492E]">{error}</p>}
            <button
              data-testid="portal-verify-btn"
              type="submit"
              disabled={busy || code.length !== 6}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-lime px-5 py-3.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-40"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <ArrowRight className="h-4 w-4" strokeWidth={1.5} />}
              {busy ? "Checking…" : "Open my portal"}
            </button>
            <button type="button" data-testid="portal-resend-btn" onClick={resend}
              className="mt-5 w-full text-xs uppercase tracking-[0.15em] text-stone transition-colors hover:text-ink">
              Send a new code
            </button>
            <p className="mt-6 text-xs leading-relaxed text-fog">
              We’ll remember this device for 30 days, so next time you can walk straight in.
            </p>
          </form>
        )}

        {state === "error" && (
          <div className="card-surface p-7" data-testid="portal-invalid">
            <p className="text-sm leading-relaxed text-stone">{error}</p>
            {retryable ? (
              <button data-testid="portal-retry-btn"
                onClick={() => { claimed.current = false; setState("checking"); setError(""); window.location.reload(); }}
                className="mt-5 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
                Try again
              </button>
            ) : (
              <p className="mt-4 text-xs text-fog">
                Please ask the studio to send you a fresh invitation link.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
